import { Box, Button, Typography } from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { addMinutes, max } from 'date-fns';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CreateVisitFormValues } from '../../organisms/create-visit/schema/create-visit-validation-schema';

import {
  CreateVisitContext,
  CreateVisitEvent,
} from '@/features/visits/utils/machine/create-visit-machine';
import { useKey } from '@/hooks/use-key';

interface Props {
  context: CreateVisitContext;
  send: (event: CreateVisitEvent) => void;
}

export const DatesInfo = ({ context, send }: Props) => {
  const { t } = useTranslation(['common', 'createVisit']);

  const { control, watch } = useFormContext<CreateVisitFormValues>();

  const expectedAt = watch('expectedAt');
  const finishesAt = watch('finishesAt');

  const nextDisabled = expectedAt >= finishesAt;

  const nextStep = () => {
    if (nextDisabled) {
      return;
    }

    send({ type: 'NEXT' });
  };

  useKey('Enter', nextStep, !nextDisabled);

  return (
    <div>
      <Typography
        color="textPrimary"
        sx={{
          mb: 5,
          mt: 2,
        }}
        variant="h6">
        {t('common:step')} {context.step}. {t(context.content)}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Controller
          control={control}
          name="expectedAt"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <MobileDateTimePicker
              slotProps={{
                textField: {
                  helperText: error?.message,
                  error: !!error?.message,
                  sx: { marginBottom: 2 },
                },
              }}
              disablePast
              label={t('createVisit:dates.expectedAt')}
              value={value}
              onAccept={onChange}
              minDateTime={new Date()}
            />
          )}
        />
        <Controller
          control={control}
          name="finishesAt"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <MobileDateTimePicker
              slotProps={{
                textField: {
                  helperText: error?.message,
                  error: !!error?.message,
                  sx: { marginBottom: 2 },
                },
              }}
              disablePast
              label={t('createVisit:dates.finishesAt')}
              value={value}
              onAccept={onChange}
              minDateTime={max([addMinutes(expectedAt, 1), new Date()])}
            />
          )}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 'auto',
          pt: 3,
        }}>
        <Button
          color="primary"
          onClick={() => send({ type: 'BACK' })}
          size="large"
          sx={{ mr: 2 }}
          type="button"
          variant="text">
          {t('common:button.back')}
        </Button>
        <Button
          color="primary"
          size="large"
          type="button"
          onClick={nextStep}
          disabled={nextDisabled}
          variant="contained">
          {t('common:button.nextStep')}
        </Button>
      </Box>
    </div>
  );
};
